import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { 
  ArrowLeft, 
  Send,
  DollarSign,
  Users,
  Calculator,
  AlertCircle,
  CheckCircle,
  UtensilsCrossed,
  Home as HomeIcon,
  Car,
  Music,
  Zap,
  ShoppingBag,
  MoreHorizontal,
} from 'lucide-react';
import { mockCategories } from '../mock/mockData';
import { toast } from 'sonner';
import { useAuth } from '../context/CompleteAuthContext';

const CATEGORY_ICONS = {
  UtensilsCrossed,
  Home: HomeIcon,
  Car,
  Music,
  Zap,
  ShoppingBag,
  MoreHorizontal,
};

const AddExpense = ({ onBack, preSelectedGroupId = null, groupData = null }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [currentGroup, setCurrentGroup] = useState(null);
  
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: 'general',
    groupId: preSelectedGroupId || '',
    paidBy: user?.id || '',
    splitMode: 'EVEN',
    customSplitType: 'amounts', // 'amounts' or 'percentages'
    selectedMembers: [],
    customAmounts: {},
    customPercentages: {},
    notes: ''
  });

  const [errors, setErrors] = useState({});

  // Format currency on blur
  const formatCurrencyValue = (value) => {
    if (!value) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return num.toFixed(2);
  };

  // Fetch groups if not pre-selected
  useEffect(() => {
    if (!preSelectedGroupId && user) {
      fetchGroups();
    } else if (preSelectedGroupId && groupData) {
      setCurrentGroup(groupData);
      setFormData(prev => ({
        ...prev,
        groupId: preSelectedGroupId,
        paidBy: user?.id || '',
        selectedMembers: groupData.members
          .filter(m => m.user_id !== user?.id && m.status === 'active')
          .map(m => m.user_id)
      }));
    }
  }, [preSelectedGroupId, groupData, user]);

  const fetchGroups = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/groups`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const handleGroupChange = async (groupId) => {
    setFormData(prev => ({ ...prev, groupId }));
    
    // Fetch group details
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentGroup(data);
        // Auto-select all active members except the payer
        setFormData(prev => ({
          ...prev,
          selectedMembers: data.members
            .filter(m => m.user_id !== prev.paidBy && m.status === 'active')
            .map(m => m.user_id)
        }));
      }
    } catch (error) {
      console.error('Error fetching group:', error);
    }
  };

  const handleInputChange = (field, value) => {
    // Special handling for paidBy changes
    if (field === 'paidBy') {
      setFormData(prev => {
        // Remove the new payer from selectedMembers if they're in it
        const updatedSelectedMembers = prev.selectedMembers.filter(id => id !== value);
        
        console.log('🔍 Payer changed:', {
          oldPayer: prev.paidBy,
          newPayer: value,
          selectedMembersBefore: prev.selectedMembers,
          selectedMembersAfter: updatedSelectedMembers
        });
        
        // Also clear custom amounts/percentages for the new payer to avoid confusion
        const updatedCustomAmounts = { ...prev.customAmounts };
        const updatedCustomPercentages = { ...prev.customPercentages };
        delete updatedCustomAmounts[value];
        delete updatedCustomPercentages[value];
        
        return {
          ...prev,
          [field]: value,
          selectedMembers: updatedSelectedMembers,
          customAmounts: updatedCustomAmounts,
          customPercentages: updatedCustomPercentages
        };
      });
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleMemberToggle = (memberId) => {
    setFormData(prev => ({
      ...prev,
      selectedMembers: prev.selectedMembers.includes(memberId)
        ? prev.selectedMembers.filter(id => id !== memberId)
        : [...prev.selectedMembers, memberId]
    }));
  };

  // Helper: Get deduplicated participants list (payer + selected members)
  const getUniqueParticipants = () => {
    const allParticipants = [formData.paidBy, ...formData.selectedMembers];
    return [...new Set(allParticipants)].filter(id => id); // Remove any null/undefined
  };

  const handleCustomAmountChange = (memberId, amount) => {
    setFormData(prev => ({
      ...prev,
      customAmounts: {
        ...prev.customAmounts,
        [memberId]: amount
      }
    }));
  };


  const handleCustomPercentChange = (memberId, percent) => {
    setFormData(prev => ({
      ...prev,
      customPercentages: {
        ...prev.customPercentages,
        [memberId]: percent
      }
    }));
  };

  const getPercentTotal = () => {
    const uniqueParticipants = getUniqueParticipants();
    let total = 0;
    for (const memberId of uniqueParticipants) {
      total += parseFloat(formData.customPercentages[memberId] || 0);
    }
    return total;
  };

  const validatePercentSplit = () => {
    if (formData.splitMode !== 'PERCENT') return true;
    const total = getPercentTotal();
    return Math.abs(total - 100) < 0.1; // Tolerance for floating point (e.g., 3x33.33=99.99)
  };

  const getPercentPreview = (memberId) => {
    if (!formData.amount) return '0.00';
    const percent = parseFloat(formData.customPercentages[memberId] || 0);
    const totalAmount = parseFloat(formData.amount);
    return ((totalAmount * percent) / 100).toFixed(2);
  };

  const getEvenSplitAmount = () => {
    if (!formData.amount || !currentGroup) return 0;
    
    const totalAmount = parseFloat(formData.amount);
    const uniqueParticipants = getUniqueParticipants();
    
    return (totalAmount / uniqueParticipants.length).toFixed(2);
  };

  const validateCustomSplit = () => {
    if (formData.splitMode !== 'CUSTOM_AMOUNTS') return true;
    
    const totalAmount = parseFloat(formData.amount);
    let customTotal = 0;
    
    const uniqueParticipants = getUniqueParticipants();
    
    for (const memberId of uniqueParticipants) {
      const amount = parseFloat(formData.customAmounts[memberId] || 0);
      customTotal += amount;
    }
    
    return Math.abs(customTotal - totalAmount) < 0.01;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }

    if (!formData.groupId) {
      newErrors.groupId = 'Please select a group';
    }

    if (!formData.paidBy) {
      newErrors.paidBy = 'Please select who paid';
    }

    if (formData.selectedMembers.length === 0) {
      newErrors.selectedMembers = 'Please select at least one other member';
    }

    if (formData.splitMode === 'CUSTOM_AMOUNTS' && !validateCustomSplit()) {
      newErrors.customAmounts = 'Custom amounts must add up to the total amount';
    }

    if (formData.splitMode === 'PERCENT' && !validatePercentSplit()) {
      newErrors.customPercentages = 'Percentages must add up to 100%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🔵 Submit clicked', {
      splitMode: formData.splitMode,
      amount: formData.amount,
      selectedMembers: formData.selectedMembers,
      paidBy: formData.paidBy,
      customPercentages: formData.customPercentages,
      percentTotal: formData.splitMode === 'PERCENT' ? getPercentTotal() : 'N/A'
    });
    
    if (!validateForm()) {
      // Show toast so user knows validation failed
      const errorKeys = Object.keys(errors);
      // Re-run validation to get fresh errors for the toast
      const freshErrors = {};
      if (!formData.description.trim()) freshErrors.description = true;
      if (!formData.amount || parseFloat(formData.amount) <= 0) freshErrors.amount = true;
      if (!formData.groupId) freshErrors.groupId = true;
      if (!formData.paidBy) freshErrors.paidBy = true;
      if (formData.selectedMembers.length === 0) freshErrors.selectedMembers = true;
      if (formData.splitMode === 'PERCENT' && !validatePercentSplit()) freshErrors.percentages = true;
      if (formData.splitMode === 'CUSTOM_AMOUNTS' && !validateCustomSplit()) freshErrors.customAmounts = true;
      
      const failedChecks = Object.keys(freshErrors).join(', ');
      console.error('❌ Validation failed:', freshErrors);
      toast.error('Please fix form errors', {
        description: `Issues with: ${failedChecks}`
      });
      return;
    }

    setIsLoading(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      // Build participants list using helper
      const uniqueParticipants = getUniqueParticipants();
      
      console.log('📤 Submitting expense with participants:', {
        payer: formData.paidBy,
        selectedMembers: formData.selectedMembers,
        uniqueParticipants: uniqueParticipants,
        totalParticipants: uniqueParticipants.length
      });
      
      const participants = uniqueParticipants.map(memberId => {
        const member = currentGroup.members.find(m => m.user_id === memberId);
        
        let owedAmount;
        if (formData.splitMode === 'EVEN') {
          owedAmount = parseFloat(getEvenSplitAmount());
        } else if (formData.splitMode === 'PERCENT') {
          // For PERCENT mode, backend will compute - send 0 as placeholder
          owedAmount = 0;
        } else {
          owedAmount = parseFloat(formData.customAmounts[memberId] || 0);
        }
        
        return {
          user_id: memberId,
          name: member?.name || 'Unknown',
          email: member?.email || '',
          owed_amount: owedAmount
        };
      });
      
      // Build splits array for PERCENT mode
      let splits = null;
      if (formData.splitMode === 'PERCENT') {
        splits = uniqueParticipants.map(memberId => ({
          user_id: memberId,
          value: parseFloat(formData.customPercentages[memberId] || 0)
        }));
      }
      
      const expenseData = {
        description: formData.description,
        amount_total: parseFloat(formData.amount),
        currency: 'USD',
        paid_by_user_id: formData.paidBy,
        split_mode: formData.splitMode,
        participants: participants,
        splits: splits,
        category: formData.category || 'general',
        notes: formData.notes || null
      };
      
      // === LOGGING POINT 1: Frontend Submit ===
      console.log('=== FRONTEND SUBMIT ===');
      console.log('Selected splitMode:', formData.splitMode);
      console.log('Full payload:', JSON.stringify(expenseData, null, 2));
      console.log('Splits array:', splits);
      console.log('========================');
      
      const response = await fetch(`${BACKEND_URL}/api/groups/${formData.groupId}/expenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(expenseData)
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('❌ Backend error response:', {
          status: response.status,
          statusText: response.statusText,
          error: error
        });
        throw new Error(error.detail || 'Failed to create expense');
      }

      const newExpense = await response.json();
      
      toast.success('Expense added successfully!', {
        description: `${formData.description} - $${formData.amount}`
      });
      
      setIsLoading(false);
      onBack();
    } catch (error) {
      console.error('Error creating expense:', error);
      toast.error('Failed to add expense', {
        description: error.message || 'Please try again'
      });
      setIsLoading(false);
    }
  };

  const getMemberName = (memberId) => {
    if (!currentGroup) return '';
    const member = currentGroup.members.find(m => m.user_id === memberId);
    return member?.name || '';
  };

  const activeMembers = currentGroup?.members.filter(m => m.status === 'active') || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center space-x-4">
            <Button 
              onClick={onBack} 
              variant="ghost" 
              size="icon"
              className="text-white hover:bg-blue-500"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Add Expense</h1>
              <p className="text-blue-100 text-sm">Split an expense with your group</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <DollarSign className="w-5 h-5" />
                <span>Expense Details</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Group Selection - Hidden if preselected */}
              {!preSelectedGroupId && (
                <div className="space-y-2">
                  <Label htmlFor="group">Group *</Label>
                  <Select 
                    value={formData.groupId} 
                    onValueChange={handleGroupChange}
                  >
                    <SelectTrigger className={errors.groupId ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select a group" />
                    </SelectTrigger>
                    <SelectContent>
                      {groups.map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.groupId && (
                    <p className="text-sm text-red-600">{errors.groupId}</p>
                  )}
                </div>
              )}

              {preSelectedGroupId && currentGroup && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    Adding expense to: <span className="font-semibold">{currentGroup.name}</span>
                  </p>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    placeholder="e.g., Dinner at Joe's"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className={errors.description ? 'border-red-500' : ''}
                  />
                  {errors.description && (
                    <p className="text-sm text-red-600">{errors.description}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount">Amount ($) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    onBlur={(e) => {
                      // Format to 2 decimals on blur
                      const value = e.target.value;
                      if (value && !isNaN(value)) {
                        const formatted = parseFloat(value).toFixed(2);
                        handleInputChange('amount', formatted);
                      }
                    }}
                    className={errors.amount ? 'border-red-500' : ''}
                  />
                  {errors.amount && (
                    <p className="text-sm text-red-600">{errors.amount}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => handleInputChange('category', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {mockCategories.map((cat) => {
                      const Icon = CATEGORY_ICONS[cat.icon];
                      return (
                        <SelectItem key={cat.id} value={cat.id}>
                          <span className="inline-flex items-center gap-2">
                            {Icon && <Icon className="w-4 h-4 text-gray-500" />}
                            <span>{cat.name}</span>
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Add any additional notes..."
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Paid By */}
          {currentGroup && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="w-5 h-5" />
                  <span>Who Paid?</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Select 
                    value={formData.paidBy} 
                    onValueChange={(value) => handleInputChange('paidBy', value)}
                  >
                    <SelectTrigger className={errors.paidBy ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select who paid" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeMembers.map((member) => (
                        <SelectItem key={member.user_id} value={member.user_id}>
                          {member.user_id === user?.id ? 'You' : member.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.paidBy && (
                    <p className="text-sm text-red-600">{errors.paidBy}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Split Configuration */}
          {currentGroup && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calculator className="w-5 h-5" />
                  <span>How to Split?</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Split Mode */}
                <div className="space-y-2">
                  <Label className="text-sm">Split Mode</Label>
                  <RadioGroup 
                    value={formData.splitMode} 
                    onValueChange={(value) => handleInputChange('splitMode', value)}
                    className="grid gap-2"
                  >
                    {[
                      { value: 'EVEN', title: 'Split Evenly', desc: 'Everyone pays the same amount' },
                      { value: 'CUSTOM_AMOUNTS', title: 'Custom Split', desc: 'Enter exact amounts for each person' },
                      { value: 'PERCENT', title: 'Percent (%)', desc: 'Enter percentages for each person' },
                    ].map((opt) => {
                      const selected = formData.splitMode === opt.value;
                      return (
                        <Label
                          key={opt.value}
                          htmlFor={opt.value}
                          className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
                            selected ? 'border-blue-500 bg-blue-50/60' : 'border-gray-200 hover:bg-gray-50'
                          }`}
                          data-testid={`split-mode-${opt.value.toLowerCase()}`}
                        >
                          <RadioGroupItem value={opt.value} id={opt.value} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{opt.title}</p>
                            <p className="text-xs text-gray-500">{opt.desc}</p>
                          </div>
                        </Label>
                      );
                    })}
                  </RadioGroup>
                </div>

                {/* Member Selection */}
                <div className="space-y-3">
                  <Label>Who is involved? (besides the payer)</Label>
                  <div className="space-y-2">
                    {activeMembers
                      .filter(m => m.user_id !== formData.paidBy)
                      .map((member) => (
                        <div 
                          key={member.user_id} 
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-center space-x-3">
                            <Checkbox
                              id={member.user_id}
                              checked={formData.selectedMembers.includes(member.user_id)}
                              onCheckedChange={() => handleMemberToggle(member.user_id)}
                            />
                            <Label 
                              htmlFor={member.user_id} 
                              className="flex items-center space-x-2 cursor-pointer font-normal"
                            >
                              <Avatar className="w-8 h-8">
                                <AvatarImage src={member.avatar} alt={member.name} />
                                <AvatarFallback>{member.name.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <span>{member.name}</span>
                            </Label>
                          </div>
                          
                          {formData.splitMode === 'EVEN' && formData.selectedMembers.includes(member.user_id) && (
                            <Badge variant="secondary" className="text-sm">
                              ${getEvenSplitAmount()}
                            </Badge>
                          )}
                        </div>
                      ))}
                  </div>
                  {errors.selectedMembers && (
                    <p className="text-sm text-red-600 flex items-center space-x-1">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errors.selectedMembers}</span>
                    </p>
                  )}
                </div>

                {/* Custom Amounts */}
                {formData.splitMode === 'CUSTOM_AMOUNTS' && formData.amount && (
                  <div className="space-y-3 mt-4 pt-4 border-t">
                    <Label>Enter custom amounts for each person</Label>
                    
                    {/* Payer's custom amount */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback>
                            {getMemberName(formData.paidBy).charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{getMemberName(formData.paidBy)} (Payer)</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          className="w-24"
                          value={formData.customAmounts[formData.paidBy] || ''}
                          onChange={(e) => handleCustomAmountChange(formData.paidBy, e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Other participants' custom amounts */}
                    {formData.selectedMembers.map((memberId) => (
                      <div key={memberId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Avatar className="w-8 h-8">
                            <AvatarFallback>
                              {getMemberName(memberId).charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{getMemberName(memberId)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            className="w-24"
                            value={formData.customAmounts[memberId] || ''}
                            onChange={(e) => handleCustomAmountChange(memberId, e.target.value)}
                          />
                        </div>
                      </div>
                    ))}

                    {/* Validation Message */}
                    {formData.amount && (
                      <div className={`flex items-center space-x-2 p-3 rounded-lg ${
                        validateCustomSplit() ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>
                        {validateCustomSplit() ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-sm">Amounts add up correctly!</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm">
                              Amounts must add up to ${formData.amount}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                    
                    {errors.customAmounts && (
                      <p className="text-sm text-red-600">{errors.customAmounts}</p>
                    )}
                  </div>
                )}


                {/* Percent Split */}
                {formData.splitMode === 'PERCENT' && (
                  <div className="space-y-3 mt-4 pt-4 border-t">
                    <Label>Enter percentages for each person</Label>
                    
                    {formData.amount ? (
                      <>
                        {/* Payer's percentage */}
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <Avatar className="w-8 h-8">
                              <AvatarFallback>
                                {getMemberName(formData.paidBy).charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium">{getMemberName(formData.paidBy)} (Payer)</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              placeholder="0.00"
                              className="w-20"
                              value={formData.customPercentages[formData.paidBy] || ''}
                              onChange={(e) => handleCustomPercentChange(formData.paidBy, e.target.value)}
                            />
                            <span className="text-sm font-medium text-gray-700">%</span>
                            <span className="text-sm text-gray-400 ml-2 w-16 text-right">
                              ${getPercentPreview(formData.paidBy)}
                            </span>
                          </div>
                        </div>

                        {/* Other participants' percentages */}
                        {formData.selectedMembers.map((memberId) => (
                          <div key={memberId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-2">
                              <Avatar className="w-8 h-8">
                                <AvatarFallback>
                                  {getMemberName(memberId).charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium">{getMemberName(memberId)}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                placeholder="0.00"
                                className="w-20"
                                value={formData.customPercentages[memberId] || ''}
                                onChange={(e) => handleCustomPercentChange(memberId, e.target.value)}
                              />
                              <span className="text-sm font-medium text-gray-700">%</span>
                              <span className="text-sm text-gray-400 ml-2 w-16 text-right">
                                ${getPercentPreview(memberId)}
                              </span>
                            </div>
                          </div>
                        ))}

                        {/* Running Total */}
                        <div className={`flex items-center justify-between p-3 rounded-lg font-medium ${
                          validatePercentSplit() ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                        }`}>
                          <span>Total:</span>
                          <span className="text-lg">{getPercentTotal().toFixed(2)}% / 100%</span>
                        </div>

                        {/* Validation Message */}
                        <div className={`flex items-center space-x-2 p-3 rounded-lg ${
                          validatePercentSplit() ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {validatePercentSplit() ? (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-sm">
                                Percentages add up correctly! Ready to submit.
                              </span>
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-4 h-4" />
                              <span className="text-sm">
                                Percentages must add up to exactly 100% (currently {getPercentTotal().toFixed(2)}%)
                              </span>
                            </>
                          )}
                        </div>
                        
                        {errors.customPercentages && (
                          <p className="text-sm text-red-600">{errors.customPercentages}</p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-500 p-3 bg-gray-50 rounded">
                        Please enter the total amount first to set percentages
                      </p>
                    )}
                  </div>
                )}


                {/* Summary */}
                {formData.amount && formData.splitMode === 'EVEN' && formData.selectedMembers.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <h4 className="font-medium text-blue-900 mb-2">Split Summary</h4>
                    <div className="space-y-1 text-sm text-blue-700">
                      <p>• Total amount: ${parseFloat(formData.amount).toFixed(2)}</p>
                      <p>• Split between: {formData.selectedMembers.length + 1} people</p>
                      <p>• Each person pays: ${getEvenSplitAmount()}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Submit Buttons */}
          <div className="flex items-center space-x-4">
            <Button 
              type="submit" 
              data-testid="add-expense-submit-btn"
              className="bg-blue-600 hover:bg-blue-700 px-8"
              disabled={isLoading || !currentGroup}
            >
              {isLoading ? 'Adding Expense...' : 'Add Expense'}
            </Button>
            <Button type="button" variant="outline" onClick={onBack} disabled={isLoading}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddExpense;
